import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Sanitize strings to prevent HTML injection by escaping HTML entities
 */
function sanitizeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Send an invitation email using Supabase direct-email function
 */
export const sendInvitationEmail = async (
  candidates: { recipientEmail: string, recipientName: string, assessmentLink: string, companyName?: string }[],
  assessmentTitle: string,
  senderName: string,
  assessmentId?: string
): Promise<boolean> => {
  try {
    console.log('📧 Sending invitation emails via Supabase direct-email:', candidates.length);
    
    // Send emails to all candidates in parallel
    const emailPromises = candidates.map(async (candidate) => {
      const templateModel = {
        name: sanitizeHtml(candidate.recipientName),
        assessmentTitle: sanitizeHtml(assessmentTitle),
        assessmentLink: candidate.assessmentLink,
        senderName: sanitizeHtml(senderName),
        companyName: sanitizeHtml(candidate.companyName || 'RoundOne')
      };
      
      // Call Supabase Edge Function directly
      const { data, error } = await supabase.functions.invoke('direct-email', {
        body: {
          to: candidate.recipientEmail,
          templateAlias: 'candidate-invitation',
          templateModel,
          from: 'hello@roundone.ai',
          replyTo: 'hello@roundone.ai'
        }
      });
      
      if (error) {
        console.error(`📧 Error sending invitation to ${candidate.recipientEmail}:`, error);
        return { success: false, email: candidate.recipientEmail, error: error.message };
      }
      
      return { success: true, email: candidate.recipientEmail };
    });
    
    const results = await Promise.all(emailPromises);
    const failures = results.filter(r => !r.success);
    
    if (failures.length > 0) {
      console.error('📧 Failed to send some invitation emails:', failures);
      toast.error(`Failed to send ${failures.length} invitation emails`);
      return failures.length < results.length; // Partial success
    }
    
    console.log('📧 All invitation emails sent successfully');
    return true;
  } catch (error: any) {
    console.error('📧 Error in sendInvitationEmail:', error);
    toast.error(error.message || 'Failed to send invitation emails');
    return false;
  }
};

/**
 * Send a welcome email to a new user using Supabase direct-email function
 */
export const sendWelcomeEmail = async (
  email: string,
  firstName: string
): Promise<boolean> => {
  try {
    console.log('📧 Sending welcome email to new user:', email);
    
    // Use a sanitized firstName or default to "there" if empty
    const safeFirstName = firstName?.trim() || "there";
    
    const { data, error } = await supabase.functions.invoke('direct-email', {
      body: {
        to: email,
        templateAlias: 'new-user-welcome',
        templateModel: { firstName: safeFirstName },
        from: 'hello@roundone.ai',
        replyTo: 'hello@roundone.ai'
      }
    });
    
    if (error) {
      console.error('❌ Error sending welcome email:', error);
      return false;
    }
    
    console.log('✅ Welcome email sent successfully');
    return true;
  } catch (error) {
    console.error('❌ Error in sendWelcomeEmail:', error);
    return false;
  }
};

/**
 * Send an ATS integration request email using Supabase direct-email function
 */
export const sendAtsRequestEmail = async (params: {
  atsName: string;
  atsWebsite: string;
  message: string;
  userName: string;
  userEmail: string;
  userId: string;
  organizationName: string;
  organizationId: string;
}): Promise<boolean> => {
  try {
    console.log('📧 Sending ATS integration request email');
    
    const { data, error } = await supabase.functions.invoke('direct-email', {
      body: {
        to: 'support@roundone.ai',
        templateAlias: 'ats-integration-request',
        templateModel: {
          atsName: params.atsName,
          atsWebsite: params.atsWebsite,
          message: params.message,
          userName: params.userName,
          userEmail: params.userEmail,
          userId: params.userId,
          organizationName: params.organizationName,
          organizationId: params.organizationId
        },
        from: 'hello@roundone.ai',
        replyTo: params.userEmail || 'hello@roundone.ai'
      }
    });
    
    if (error) {
      console.error('❌ Error sending ATS integration request email:', error);
      return false;
    }
    
    console.log('✅ ATS integration request email sent successfully');
    return true;
  } catch (error) {
    console.error('❌ Error in sendAtsRequestEmail:', error);
    return false;
  }
};
